import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import applicationsAPI from 'api/applicationsAPI';
import roleSearchAPI from 'api/roleSearchAPI';
import usersAPI from 'api/usersAPI';
import { User, JobviewJob, HasApplicationStatus, CandidateApplicationConnectData } from 'types';
import hiringRequestsAPI from 'api/hiringRequestsAPI';

import { ApplicationStatus, ExistingHiringRequestData } from 'types/application';
import { HiringRequestListAPIData, NewHiringRequestsAPIResponse } from '../types';
import applicationStatusesAPI from 'api/applicationStatusesAPI';
import { appStatusType } from 'utils/enums';
import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import { AxiosError } from 'axios';

export const fetchActiveRoles = (inputValue: string): Promise<JobviewJob[]> => {
  return roleSearchAPI.searchActiveDistrictRoles(inputValue).then((res) => res.results);
};

export const useQuerySearchActiveRoles = (
  inputValue: string,
  options: Partial<UseQueryOptions<JobviewJob[]>> = {}
): UseQueryResult<JobviewJob[]> =>
  useQuery({
    ...options,
    queryKey: ['activeRoleSearchResults', inputValue],
    queryFn: () => fetchActiveRoles(inputValue),
  });

export const fetchCandidate = (candidateId: number): Promise<User> => {
  return usersAPI.fetchUser(candidateId).then((res) => res);
};

export const useQueryFetchCandidateForConnectATSIntegration = (
  candidateId: number
): UseQueryResult<User> => {
  return useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => fetchCandidate(candidateId),
    retry: false,
    enabled: Number.isInteger(candidateId),
  });
};

export const fetchIfCandidateHasApplicationForRole = (
  roleId: number,
  candidateId: number
): Promise<HasApplicationStatus> => {
  return applicationsAPI.checkIfCandidateHasApplication(candidateId, roleId).then((res) => {
    return res;
  });
};

export const useQueryCheckIfCandidateHasApplication = (
  candidateId: number,
  roleId: number,
  enabled: boolean
): UseQueryResult<HasApplicationStatus> => {
  const queryKeyToUse = ['roleId', roleId, 'candidateId', candidateId];

  return useQuery({
    queryKey: queryKeyToUse,
    queryFn: () => fetchIfCandidateHasApplicationForRole(roleId, candidateId),
    enabled,
  });
};

export const useCheckForExistingHiringRequest = (
  districtId: number,
  candidateId: number,
  options: Partial<UseQueryOptions<ExistingHiringRequestData>> = {}
): UseQueryResult<ExistingHiringRequestData> => {
  return useQuery({
    queryFn: async () => await hiringRequestsAPI.checkForExistingByCandidateId(candidateId),
    queryKey: ['hiringRequests', 'checkForExisting', { districtId, candidateId }],
    ...options,
  });
};

export const createHiringRequestFromConnectWithNewEndpoint = (
  payload: CandidateApplicationConnectData
): NewHiringRequestsAPIResponse => {
  return hiringRequestsAPI.importCandidateFromConnect(payload).then((res) => res);
};

export const getPostHiringPoolApplicationStatuses = (districtId: number) => {
  return applicationStatusesAPI.getAll({ districtId, statusTypes: appStatusType.post_hiring_pool });
};

export const useQueryGetPostHiringPoolStatuses = (
  districtId: number
): UseQueryResult<ApplicationStatus[]> => {
  return useQuery({
    queryKey:
      hiringRequestQueries.hiringRequests.postHiringPoolApplicationStatuses(districtId).queryKey,
    queryFn: () => getPostHiringPoolApplicationStatuses(districtId),
    retry: false,
    enabled: Number.isInteger(districtId),
  });
};

export const useQueryGetHiringRequestListData = (
  districtId: number,
  options?: Omit<
    UseInfiniteQueryOptions<HiringRequestListAPIData, AxiosError>,
    'queryKey' | 'queryFn'
  >
): UseInfiniteQueryResult<HiringRequestListAPIData, AxiosError> => {
  return useInfiniteQuery({
    queryKey: [...hiringRequestQueries.hiringRequests.list(districtId).queryKey],
    queryFn: ({ pageParam = 1 }) =>
      hiringRequestsAPI.getHiringRequests(pageParam).then((res) => res),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      return new URL(lastPage.next).searchParams.get('page') || undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};
